// Vendor Billing — vendor-portal invoice reads + Stripe pay flow. Scoped
// server-side by the authenticated account's PortalUser.LinkedVendorId
// (never package claims — vendors hold zero CollectionPackages).

import { portalApi } from "./client";

export type PortalVendorInvoiceStatus =
  | "issued"
  | "viewed"
  | "processing"
  | "paid"
  | "overdue"
  | "void";

/** Vendor-facing payment-history row (detail reads only). */
export interface PortalVendorInvoicePaymentDto {
  amount: number;
  receivedAt: string;
  method: string;
  /** Set when Phoenix reversed the receipt — a vendor whose invoice went back
   *  to unpaid should see why, not find a payment still listed against a
   *  balance they now owe. The staff reason stays internal. */
  reversedAt?: string | null;
}

export interface PortalVendorInvoiceDto {
  id: string;
  invoiceNumber: string;
  /** Borrower/business display name — vendors never see Phoenix loan numbers
   *  unless no borrower resolves ("Loan <number>" fallback). */
  loanDisplayName: string;
  amount: number;
  description: string | null;
  status: PortalVendorInvoiceStatus;
  issuedAt: string | null;
  dueDate: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  payments?: PortalVendorInvoicePaymentDto[] | null;
}

/** What the Stripe Payment Element mounts with. The publishable key is the
 *  only Stripe key the server ever hands to the browser. */
export interface VendorInvoicePaymentIntentDto {
  paymentIntentId: string;
  clientSecret: string;
  publishableKey: string;
  status: string;
  amount: number;
}

// ── Vendor Program v2 — monthly reports + performance (PR 2) ────────────
// Mirrors VendorReportDtos.cs / VendorComplaintDtos.cs (camelCase over the
// ApiResponse envelope). The report DTO carries all four sections — the
// filing vendor sees everything; only §1 + §2 ever reach the borrower.

export type VendorReportStatus =
  | "draft"
  | "submitted"
  | "returned"
  | "accepted"
  | "signed-off"
  | "void";

export interface VendorReportGoalGradeDto {
  goalId: string;
  goalTitle: string;
  completionCriteria: string | null;
  targetMonths: string | null;
  score: number;
  isComplete: boolean;
  note: string | null;
}

/** F11 regulated (borrower-paid) §4 line — private to Phoenix, like the
 *  invoice fields it replaces for regulated vendors. */
export interface VendorReportCollectedLineItemDto {
  id: string;
  amount: number;
  serviceDescription: string;
}

export interface VendorMonthlyReportDto {
  id: string;
  assignmentId: string;
  loanId: string;
  loanNumber: string;
  vendorId: string;
  vendorName: string;
  roleOnLoan: string;
  periodStart: string;
  periodEnd: string;
  status: VendorReportStatus;
  revisionNumber: number;
  publicNarrative: string | null;
  goalGrades: VendorReportGoalGradeDto[];
  privateConcerns: string | null;
  selfReportedInvoiceAmount: number | null;
  amountCalculationExplanation: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  signedOffAt: string | null;
  latestReviewComment: string | null;
  // F11 regulated (borrower-paid) — when true the form's §4 swaps the
  // invoice inputs for the fees-for-services lines (billed to the borrower
  // through Phoenix — SP-4 WS-3). Optional so pre-existing fixtures stay
  // valid; the server always sends them.
  isRegulatedVendor?: boolean;
  collectedLineItems?: VendorReportCollectedLineItemDto[] | null;
  collectedNothingConfirmed?: boolean;
  // SP-4 WS-2 — the §4 quantity behind the amount (hours worked / total
  // transaction value) + the vendor's effective contract-term basis
  // ("FlatPerEngagement" | "Hourly" | "PercentOfValue", null = no structured
  // terms). The quantity input renders only for hourly/percent terms.
  reportedQuantity?: number | null;
  contractTermBasis?: string | null;
}

export interface PortalVendorReportListItemDto {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: VendorReportStatus;
  revisionNumber: number;
  submittedAt: string | null;
  latestReviewComment: string | null;
}

export interface PortalVendorAssignmentReportsDto {
  assignmentId: string;
  loanId: string;
  loanLabel: string;
  roleOnLoan: string;
  reports: PortalVendorReportListItemDto[];
  /** ISO month starts for which a report is still expected. */
  outstandingPeriods: string[];
}

export interface VendorReportGradeInput {
  goalId: string;
  score: number;
  isComplete: boolean;
  note: string | null;
}

export interface VendorReportCollectedLineInput {
  amount: number;
  serviceDescription: string;
}

/** Autosave payload — always the FULL form (FIND-12 discipline). The
 *  collected fields ride every payload; the server only reads them for
 *  regulated vendors. */
export interface UpdateVendorReportRequest {
  publicNarrative: string | null;
  privateConcerns: string | null;
  selfReportedInvoiceAmount: number | null;
  amountCalculationExplanation: string | null;
  goalGrades: VendorReportGradeInput[];
  collectedLineItems: VendorReportCollectedLineInput[];
  collectedNothingConfirmed: boolean;
  /** SP-4 WS-2 — hours worked / total transaction value (per the vendor's
   *  contract-term basis). Optional; never a submit gate. */
  reportedQuantity?: number | null;
}

export interface CreateVendorComplaintRequest {
  assignmentId: string;
  reportId?: string | null;
  text: string;
}

export interface VendorPerformanceAreaDto {
  overall: number | null;
  serviceQuality: number | null;
  responsiveness: number | null;
  satisfaction: number | null;
  signOffCount: number;
  /** How many of `signOffCount` Phoenix recorded on a borrower's
   *  behalf. They count toward the averages — still the borrower's
   *  rating — but the surface says how many were transcribed. */
  staffRecordedCount: number;
}

export interface VendorPerformanceTrendPointDto {
  month: string;
  overall: number;
  count: number;
}

export interface VendorPerformanceCustomerDto {
  loanId: string;
  loanNumber: string;
  roleOnLoan: string;
  ratings: VendorPerformanceAreaDto;
}

export interface VendorPerformanceFeedbackDto {
  periodStart: string;
  loanNumber: string;
  overallRating: number;
  feedback: string;
}

export interface VendorPerformanceSummaryDto {
  overall: VendorPerformanceAreaDto;
  monthlyTrend: VendorPerformanceTrendPointDto[];
  perCustomer: VendorPerformanceCustomerDto[];
  recentFeedback: VendorPerformanceFeedbackDto[];
}

// ── Journey-Stage Adaptive Dashboards — Vendor Home ──────────────────────
// The one net-new read the Home dashboard needs. Mirrors
// PortalVendorQualificationDto / VendorQualificationStageDto. Advisory: the
// stages gate nothing — Home uses them only to place the vendor on the
// journey spine and to teach the next incomplete readiness step.

export type VendorQualificationStatus =
  | "pending"
  | "in-progress"
  | "complete"
  | "waived"
  | "not-applicable";

// Portal-shaped on purpose (audit F-02): the staff stage DTO's `note`
// (Phoenix-private vetting commentary) and `evidenceUrl` never reach the
// vendor's browser — the server strips them, so the type omits them.
export interface VendorQualificationStageDto {
  /** One of the fixed catalog codes: background-check / insurance-verification
   *  / reference-checks / capability-assessment / w9-collection. */
  stageCode: string;
  status: VendorQualificationStatus;
  statusDate: string | null;
}

export interface PortalVendorQualificationDto {
  /** Full 5-stage checklist in catalog order (pending where unrecorded). */
  stages: VendorQualificationStageDto[];
  /** True when every stage is complete / waived / not-applicable. */
  qualificationComplete: boolean;
  /** Audit F-25 — the SERVER-classified journey stage
   *  (onboarding / portal-active / connected / servicing). The single stage
   *  source: narrow with `asVendorStage`, never re-derive client-side. */
  stage: string;
  /** Audit F-35 — Vendor.IsRegulated. A regulated vendor's service fees are
   *  billed to the borrower through Phoenix (they never see invoices here),
   *  so Home copy must not promise vendor-visible invoices. */
  isRegulated: boolean;
}

// ── Step 5 W6 — vendor self-service contact details ─────────────────────
// These are VENDOR-RECORD fields, not PortalUser fields. The settings screen
// shows them in their own section, below the shared login block.

export interface PortalVendorProfileDto {
  name: string;
  phone: string | null;
  address: string | null;
  /** Read-only here. It is the sign-in credential, and changing it consumes
   *  every outstanding set-password / reset token on the account, so only
   *  Phoenix can change it. */
  email: string | null;
}

/** FIND-12 on the wire: omit a field to leave it alone, send "" to clear it.
 *  The form sends all three every time. There is deliberately no `email` —
 *  the server 400s any request that carries one. */
export interface UpdateVendorPortalProfileRequest {
  name: string;
  phone: string;
  address: string;
}

export const portalVendorApi = {
  listInvoices: () =>
    portalApi.get<PortalVendorInvoiceDto[]>("/vendor/invoices"),
  /** The first detail open stamps the invoice as viewed (one-way). */
  getInvoice: (invoiceId: string) =>
    portalApi.get<PortalVendorInvoiceDto>(`/vendor/invoices/${invoiceId}`),
  /** Creates (or reuses — one open intent per invoice) the PaymentIntent
   *  for the full invoice amount. */
  createPaymentIntent: (invoiceId: string) =>
    portalApi.post<VendorInvoicePaymentIntentDto>(`/vendor/invoices/${invoiceId}/pay`),
  /** Post-confirmation sync so card payments show paid (and ACH shows
   *  processing) without waiting for the webhook. */
  syncPayment: (invoiceId: string) =>
    portalApi.post<PortalVendorInvoiceDto>(`/vendor/invoices/${invoiceId}/payment-sync`),

  // Monthly reports
  listReports: () =>
    portalApi.get<PortalVendorAssignmentReportsDto[]>("/vendor/reports"),
  /** Idempotent get-or-create of the draft for (assignment, period). */
  createReportDraft: (assignmentId: string, periodStart: string) =>
    portalApi.post<VendorMonthlyReportDto>("/vendor/reports/draft", {
      assignmentId,
      periodStart,
    }),
  getReport: (reportId: string) =>
    portalApi.get<VendorMonthlyReportDto>(`/vendor/reports/${reportId}`),
  /** Autosave — sends the FULL payload every time. */
  updateReport: (reportId: string, body: UpdateVendorReportRequest) =>
    portalApi.put<VendorMonthlyReportDto>(`/vendor/reports/${reportId}`, body),
  /** 400 with a message when §1 or §4 is missing. */
  submitReport: (reportId: string) =>
    portalApi.post<VendorMonthlyReportDto>(`/vendor/reports/${reportId}/submit`),
  /** Routes to Phoenix immediately — never waits for the report. */
  createComplaint: (body: CreateVendorComplaintRequest) =>
    portalApi.post<unknown>("/vendor/complaints", body),

  // Performance
  getPerformance: () =>
    portalApi.get<VendorPerformanceSummaryDto>("/vendor/performance"),

  /** Journey-Stage Home read: the vendor's advisory qualification stages +
   *  the derived completion rollup used to place them on the journey spine. */
  getQualification: () =>
    portalApi.get<PortalVendorQualificationDto>("/vendor/qualification"),

  /** On-demand monthly invoice-statement PDF blob (period = YYYY-MM). */
  statement: (period: string) =>
    portalApi.getBlob("/vendor/invoices/statement", { period }),

  /** Step 5 W6 — the vendor's own contact details, off the vendor record. */
  getProfile: () => portalApi.get<PortalVendorProfileDto>("/vendor/profile"),
  /** Applies immediately; audited, and Phoenix is notified. */
  updateProfile: (body: UpdateVendorPortalProfileRequest) =>
    portalApi.put<PortalVendorProfileDto>("/vendor/profile", body),
};
